/**
 * chatEventsLogger.js — Rê Leve
 *
 * Camada de captura de eventos do chat + pedido. Publica TUDO via liveSync
 * para que o painel do operador receba em tempo real (BroadcastChannel +
 * storage event + custom event).
 *
 * Em produção, também envia para /api/events do backend (não-bloqueante).
 */

import { publish, EVT, getBuffer, clearBuffer } from './liveSyncEngine';

// ── Valores que parecem intent mas são na verdade source/status ───────────
// Quando isso é gravado como intent, o painel mostra coisas erradas tipo
// "intent: fallback" no card de intents. Tratamos como ausência de intent.
const NON_INTENT_VALUES = new Set([
  'fallback',
  'llm',
  'deterministic',
  'local',
  'backend',
  'unknown',
  '',
]);

function sanitizeIntent(raw) {
  if (!raw) return null;
  const s = String(raw).toLowerCase().trim();
  if (NON_INTENT_VALUES.has(s)) return null;
  return raw;
}

// ── Chat message ───────────────────────────────────────────────────────────
export function recordChatEvent(evt) {
  const type = evt.role === 'user' ? EVT.CHAT_USER : EVT.CHAT_BOT;
  const payload = {
    role:        evt.role || 'bot',
    text:        evt.text || '',
    clienteId:   evt.clienteId || 'visitante',
    customerName: evt.customerName || null,
    intent:      sanitizeIntent(evt.intent),
    source:      evt.source || 'local',
    activeGuardrailIds: evt.activeGuardrailIds || [],
    useCase:     evt.useCase || 'sales_relationship',
  };
  const event = publish(type, payload);
  sendToBackend(event);
  return event;
}

// ── Pedido confirmado ──────────────────────────────────────────────────────
export function recordOrderClosed({ clienteId, customerName, kitId, kitName, kitPrice, upsells = [], crossSells = [], total }) {
  const payload = {
    clienteId, customerName, kitId, kitName, kitPrice,
    upsells, crossSells, total,
    upsellTotal:     upsells.reduce((s, u) => s + (u.price || 0), 0),
    crossSellTotal:  crossSells.reduce((s, u) => s + (u.price || 0), 0),
    closedAt: new Date().toISOString(),
  };
  const event = publish(EVT.ORDER_CLOSED, payload);
  sendToBackend(event);
  return event;
}

// ── Upsell aceito (item adicionado ao kit já escolhido) ────────────────────
export function recordUpsellAdded({ clienteId, customerName, kitName, item, price }) {
  const event = publish(EVT.ORDER_UPSELL_ADDED, {
    clienteId, customerName, kitName,
    item, price,
    at: new Date().toISOString(),
  });
  sendToBackend(event);
  return event;
}

// ── Cross-sell aceito ──────────────────────────────────────────────────────
export function recordCrossSellAdded({ clienteId, customerName, suggestedItem, price }) {
  const event = publish(EVT.ORDER_CROSS_SELL_ADDED, {
    clienteId, customerName, suggestedItem, price,
    at: new Date().toISOString(),
  });
  sendToBackend(event);
  return event;
}

// ── Aprovação pendente nasce ───────────────────────────────────────────────
export function recordApprovalPending({ questionId, customerName, question }) {
  const event = publish(EVT.APPROVAL_PENDING, {
    questionId, customerName, question,
    at: new Date().toISOString(),
  });
  sendToBackend(event);
  return event;
}

// ── Aprovação respondida pela equipe ───────────────────────────────────────
export function recordApprovalAnswered({ questionId, customerName, answer, tags = [] }) {
  const event = publish(EVT.APPROVAL_ANSWERED, {
    questionId, customerName, answer, tags,
    at: new Date().toISOString(),
  });
  sendToBackend(event);
  return event;
}

// ── Hook backend (não-bloqueante) ──────────────────────────────────────────
function sendToBackend(event) {
  try {
    const apiBase = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL) || '';
    const enabled = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_ENABLE_REAL_API === 'true');
    if (!apiBase || !enabled) return;
    fetch(apiBase.replace(/\/$/, '') + '/api/events/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
      keepalive: true,
    }).catch(() => {});
  } catch {}
}

// ── Helpers de leitura (compatibilidade com código antigo) ─────────────────
export function getChatEvents({ limit = 100, role, useCase, type } = {}) {
  let events = getBuffer({ limit: 500 });

  // Compatibilidade: filtrar só os do chat
  if (!type) {
    events = events.filter(e =>
      e.type === EVT.CHAT_USER || e.type === EVT.CHAT_BOT
    );
  } else {
    events = events.filter(e => e.type === type);
  }
  if (role)    events = events.filter(e => (e.payload?.role) === role);
  if (useCase) events = events.filter(e => (e.payload?.useCase) === useCase);

  // Achatar payload no top-level para o painel antigo continuar funcionando
  return events.slice(0, limit).map(e => ({
    ts: e.ts,
    timestamp: e.timestamp,
    type: e.type,
    role: e.payload?.role,
    text: e.payload?.text,
    clienteId: e.payload?.clienteId,
    customerName: e.payload?.customerName,
    intent: e.payload?.intent,
    source: e.payload?.source,
    activeGuardrailIds: e.payload?.activeGuardrailIds || [],
    useCase: e.payload?.useCase,
  }));
}

export function clearChatEvents() {
  clearBuffer();
}

// ── Estatísticas agregadas (refeitas pra contar certo!) ────────────────────
export function getChatStats({ windowMs = 24 * 3600 * 1000 } = {}) {
  const all = getBuffer({ limit: 1000 });
  const now = Date.now();
  const recent = all.filter(e => now - new Date(e.ts).getTime() <= windowMs);

  const stats = {
    // Contadores SEPARADOS (resolve o bug do "conta 2")
    questions: all.filter(e => e.type === EVT.CHAT_USER).length,
    answers:   all.filter(e => e.type === EVT.CHAT_BOT).length,
    orders:    all.filter(e => e.type === EVT.ORDER_CLOSED).length,
    upsells:   all.filter(e => e.type === EVT.ORDER_UPSELL_ADDED).length,
    crossSells: all.filter(e => e.type === EVT.ORDER_CROSS_SELL_ADDED).length,
    pendingCreated: all.filter(e => e.type === EVT.APPROVAL_PENDING).length,
    pendingAnswered: all.filter(e => e.type === EVT.APPROVAL_ANSWERED).length,

    total: all.length,
    recent: recent.length,
    lastEventAt: all[0]?.ts || null,

    intents: {},
    sources: {},
    guardHits: {},
    useCases: {},

    // Pedidos detalhados
    orderList: [],
    upsellRevenue: 0,
    crossSellRevenue: 0,
    orderRevenue: 0,
  };

  all.forEach(e => {
    if (e.type === EVT.CHAT_USER || e.type === EVT.CHAT_BOT) {
      const p = e.payload || {};
      if (p.intent)  stats.intents[p.intent]  = (stats.intents[p.intent]  || 0) + 1;
      if (p.source)  stats.sources[p.source]  = (stats.sources[p.source]  || 0) + 1;
      if (p.useCase) stats.useCases[p.useCase] = (stats.useCases[p.useCase] || 0) + 1;
      (p.activeGuardrailIds || []).forEach(g => {
        stats.guardHits[g] = (stats.guardHits[g] || 0) + 1;
      });
    }
    if (e.type === EVT.ORDER_CLOSED) {
      const p = e.payload || {};
      stats.orderList.push({
        at: p.closedAt || e.ts,
        timestamp: e.timestamp,
        customerName: p.customerName || p.clienteId,
        clienteId: p.clienteId,
        kitName: p.kitName,
        kitPrice: p.kitPrice || 0,
        total: p.total || 0,
        upsells: p.upsells || [],
        crossSells: p.crossSells || [],
        upsellTotal: p.upsellTotal || 0,
        crossSellTotal: p.crossSellTotal || 0,
      });
      stats.orderRevenue += (p.total || 0);
    }
    if (e.type === EVT.ORDER_UPSELL_ADDED) {
      stats.upsellRevenue += (e.payload?.price || 0);
    }
    if (e.type === EVT.ORDER_CROSS_SELL_ADDED) {
      stats.crossSellRevenue += (e.payload?.price || 0);
    }
  });

  return stats;
}

// Re-export para compatibilidade
export { EVT, getBuffer, clearBuffer };
